import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import axiosInstance from '../utils/axiosInstance';
import authService from '../services/authService';
import { useAuth } from './AuthContext';

/**
 * Permission Context
 *
 * Drži EFEKTIVNE dozvole prijavljenog korisnika (ključeve koje je backend
 * već izračunao: RoleDefaults + Allow override - Deny override).
 *
 * IZUZETAK - administrator: on po definiciji ima SVE dozvole, pa se za njega
 * GET /api/Permission/me uopšte ne poziva. `can`/`canAny` odmah vraćaju true,
 * a `loading` je false. Time se izbegava nepotreban request i blokada menija
 * i ruta ("Učitavanje...") za nalog koji ionako sme sve.
 *
 * Frontend gating je SAMO UX - bezbednost je na backendu (403).
 */
const PermissionContext = createContext(null);

/**
 * Custom hook to use Permission Context.
 * @throws {Error} If used outside of PermissionProvider
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

/**
 * Permission Provider Component
 * Učitava dozvole sa GET /api/Permission/me posle logina i resetuje ih na logout.
 */
export const PermissionProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // Administrator ima sve dozvole -> dozvole se ne učitavaju sa servera.
  // `user` je već dekodiran JWT iz AuthContext-a, pa nema ponovnog dekodiranja.
  const isAdmin = useMemo(
    () => authService.getUserRole(user) === 'administrator',
    [user]
  );

  const [permissions, setPermissions] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Učitaj efektivne dozvole prijavljenog korisnika.
   * Za administratora je ovo kratak spoj - nema API poziva.
   */
  const refetch = useCallback(async () => {
    if (authService.getUserRole() === 'administrator') {
      setPermissions(new Set());
      setError(null);
      setLoaded(true);
      return;
    }

    setLoaded(false);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/Permission/me');
      const data = response.data?.data ?? response.data;
      const list = Array.isArray(data?.permissions) ? data.permissions : [];
      // Backend vraća niz OBJEKATA: [{ permission, name, label }, ...].
      // Podržavamo i goli niz stringova radi kompatibilnosti.
      const keys = list
        .map((p) => (typeof p === 'string' ? p : p?.permission))
        .filter(Boolean);
      setPermissions(new Set(keys));
    } catch (err) {
      console.error('Greška pri učitavanju dozvola:', err);
      setError(err.response?.data?.message || 'Nije moguće učitati dozvole.');
      setPermissions(new Set());
    } finally {
      setLoaded(true);
    }
  }, []);

  // Učitaj dozvole kada se korisnik prijavi; resetuj kada se odjavi.
  useEffect(() => {
    if (isAuthenticated) {
      refetch();
    } else {
      setPermissions(new Set());
      setError(null);
      setLoaded(false);
    }
  }, [isAuthenticated, refetch]);

  // Dok je korisnik prijavljen a dozvole još nisu stigle -> loading.
  // Ovako se izbegava FOUC (meni se ne renderuje sa praznim dozvolama).
  // Administrator nikad nije u loading stanju - on ima sve dozvole odmah.
  const loading = isAuthenticated && !loaded && !isAdmin;

  /**
   * Da li korisnik ima tačno zadatu dozvolu.
   * Administrator -> uvek true.
   * @param {string} permission
   * @returns {boolean}
   */
  const can = useCallback(
    (permission) => isAdmin || permissions.has(permission),
    [isAdmin, permissions]
  );

  /**
   * Da li korisnik ima BILO KOJU od zadatih dozvola.
   * Administrator -> uvek true.
   * Prazna lista -> true (ruta bez zahteva je dozvoljena).
   * @param {...string} required
   * @returns {boolean}
   */
  const canAny = useCallback(
    (...required) => {
      if (isAdmin) return true;
      if (!required || required.length === 0) return true;
      return required.some((p) => permissions.has(p));
    },
    [isAdmin, permissions]
  );

  const value = useMemo(
    () => ({ permissions, can, canAny, refetch, loading, error, isAdmin }),
    [permissions, can, canAny, refetch, loading, error, isAdmin]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
